/**
 * Core FHIR REST API service.
 *
 * Single implementation of FHIR R4 REST: get, put, patch, create, search, delete for any resource type. All
 * resource-specific services delegate to this. Uses FhirApiClientService with FHIR_API_BASE_URL.
 */

import { HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { FhirApiClientService } from '../../http/fhir-api-client.service';
import { SKIP_LOADING_INDICATOR } from '../../http/loading-context.tokens';
import { type FhirBundle, type FhirResourceBase, toSearchParams } from './fhir-api.types';

export interface FhirApiRequestOptions {
  context?: HttpContext;
  /**
   * When true, skips the global full-screen loading indicator for this request via the `SKIP_LOADING_INDICATOR`
   * HttpContext token.
   *
   * NOTE: unlike the assessment API's generated clients, this deliberately does NOT also set the `X-Skip-Loading`
   * header — `FHIR_API_BASE_URL` points at an EXTERNAL third-party FHIR service
   * (`apidev.complexcaresolutions.com/symphony-fhir`) whose CORS `Access-Control-Allow-Headers` policy we don't
   * control/can't edit. Adding an unrecognized custom header there fails the browser's CORS preflight entirely (request
   * never reaches the server), which is worse than the loader occasionally showing when this app runs as a Module
   * Federation remote inside web.shell.
   */
  skipLoadingIndicator?: boolean;
}

@Injectable()
export class FhirApiService {
  private readonly api = inject(FhirApiClientService);

  private buildRequestOptions(options?: FhirApiRequestOptions): { context?: HttpContext } {
    if (!options?.skipLoadingIndicator) {
      return { context: options?.context };
    }
    return {
      context: (options.context ?? new HttpContext()).set(SKIP_LOADING_INDICATOR, true),
    };
  }

  /** Read a resource by type and id. GET {resourceType}/{id} */
  get<T>(resourceType: string, id: string, options?: FhirApiRequestOptions): Observable<T> {
    return this.api.get<T>(`${resourceType}/${id}`, this.buildRequestOptions(options));
  }

  /**
   * Create or update a resource (FHIR PUT is idempotent). PUT {resourceType}/{id} – resource must have resourceType and
   * id.
   */
  put<T extends FhirResourceBase>(resource: T, options?: FhirApiRequestOptions): Observable<T> {
    const id = resource.id;
    if (!id) {
      throw new Error(`${resource.resourceType}.id is required for PUT`);
    }
    return this.api.put<T>(`${resource.resourceType}/${id}`, resource, this.buildRequestOptions(options));
  }

  /**
   * Partial update: GET resource, shallow-merge partial, PUT. Use when server does not support HTTP PATCH or for simple
   * merge semantics.
   */
  patch<T extends FhirResourceBase>(
    resourceType: string,
    id: string,
    partial: Partial<T>,
    options?: FhirApiRequestOptions
  ): Observable<T> {
    return this.get<T>(resourceType, id, options).pipe(
      switchMap((current) => {
        const merged = {
          ...current,
          ...partial,
          resourceType,
          id: (current as FhirResourceBase).id ?? id,
        } as T;
        return this.put(merged as T & FhirResourceBase, options);
      })
    );
  }

  /** Create a resource (server may assign id). POST {resourceType} */
  create<T extends FhirResourceBase>(resource: T, options?: FhirApiRequestOptions): Observable<T> {
    return this.api.post<T>(resource.resourceType, resource, this.buildRequestOptions(options));
  }

  /** Search resources. GET {resourceType}?... */
  search<T>(
    resourceType: string,
    params?: Record<string, string | number | boolean | string[] | undefined> | object,
    options?: FhirApiRequestOptions
  ): Observable<FhirBundle<T>> {
    const query = toSearchParams(params);
    return this.api.get<FhirBundle<T>>(resourceType, {
      params: query,
      ...this.buildRequestOptions(options),
    });
  }

  /** Delete a resource. DELETE {resourceType}/{id} */
  delete(resourceType: string, id: string, options?: FhirApiRequestOptions): Observable<void> {
    return this.api.delete<void>(`${resourceType}/${id}`, this.buildRequestOptions(options));
  }
}
